import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../shared/api/client";

/**
 * The write half of every release drawer.
 *
 * <p>Exists mainly for the 409 (§70). A release is worked on by several people at once during a
 * deployment window, so a stale write is an ordinary event rather than an edge case, and it must
 * never be silently retried with the newer version — that is how somebody's decision gets
 * overwritten by a form that was open in another tab. The conflict is reported in its own words
 * and the caller is asked to look again.
 *
 * <p>Nothing here is optimistic (§69). Approvals, QA sign-off, freezing and gate overrides all
 * change what people are allowed to do next, and showing them as done before the server agrees
 * would mean a screen that briefly lies about whether a release may ship.
 */
export function useReleaseMutation(releaseId: string) {
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const reset = () => {
    setError(null);
    setConflict(false);
  };

  const run = async (action: () => Promise<unknown>, onDone?: () => void) => {
    setSaving(true);
    reset();
    try {
      await action();
      void queryClient.invalidateQueries({ queryKey: ["release", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-readiness", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-transitions", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-scope", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-history", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-prerequisites", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-testing", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-approvals", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-execution", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-rollback", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-validation", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-observation", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-known-issues", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-deployments", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-documents", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-notes", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-fields", releaseId] });
      void queryClient.invalidateQueries({ queryKey: ["release-scope-changes", releaseId] });
      onDone?.();
      return true;
    } catch (caught) {
      /*
       * Only a genuine stale-copy conflict gets the refresh notice.
       *
       * <p>Four different things answer 409: somebody else edited the row, a duplicate already
       * exists, a membership overlaps, or the release is in a state that refuses this. Treating
       * them all as a stale copy told people to press Refresh when the real answer was "that
       * version number is already taken" — and Refresh cannot fix that, so the message sent them
       * round a loop with the one sentence that would have helped hidden behind it.
       */
      if (caught instanceof ApiError && caught.code === "CONCURRENT_MODIFICATION") {
        setConflict(true);
        setError(caught.message);
      } else {
        setError(caught instanceof ApiError ? caught.message : "Could not save that.");
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { run, error, conflict, saving, reset };
}
